import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, input, OnInit, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MarkantElementsModule } from '@markant/elements-angular';
import { BookingDataWithUpgradeInfo, Cartridge, CartridgeVariant, Price } from '@markant-platform/cartridge/model';
import { Observable } from 'rxjs';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CurrencySymbols, MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControlErrorPipe, FormControlKeyErrorPipe } from '@markant-platform/shared/util-pipes';
import { NOT_APPLICABLE, UpgradeServiceVariantForm } from '../upgrade-forms.config';
import { OrganizationDetailData } from 'data-model';

@Component({
  selector: 'markant-platform-upgrade-service-variants',
  templateUrl: './upgrade-service-variants.component.html',
  styleUrls: ['./upgrade-service-variants.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MarkantElementsModule,
    TranslocoModule,
    ReactiveFormsModule,
    FormControlKeyErrorPipe,
    FormControlErrorPipe,
  ],
})
export class UpgradeServiceVariantsComponent implements OnInit {
  form = input.required<UpgradeServiceVariantForm>();
  mode = input.required<'read' | 'edit'>();

  booking = input<BookingDataWithUpgradeInfo>();
  organization = input<OrganizationDetailData>();
  showErrors = input<boolean>();
  upgradeCandidates = input<CartridgeVariant[]>();
  service = input<Cartridge>();

  selectedVariant = signal<CartridgeVariant | undefined>(undefined);
  selectedVariantPrice = computed(() => this.getSelectedVariantPrice(this.selectedVariant()));

  billingViaPlatformOptions: MarkantSelectOption[] = this.getBillingViaPlatformOptions();
  billingViaPlatoformLabelRecord: Record<string, string> = this.billingViaPlatformOptions.reduce(
    (acc: Record<string, string>, option) => {
      acc[option.value] = option.representation;
      return acc;
    },
    {}
  );
  upgradableVariantOptions: MarkantSelectOption[] = [];

  activeLanguage$: Observable<string> = this.cmsLanguageService.activeCMSLanguage$;

  currencySymbols = CurrencySymbols;
  NOT_APPLICABLE = NOT_APPLICABLE;

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    if (this.mode() === 'edit') {
      this.upgradableVariantOptions = this.getUpgradableVariantOptions();
      this.observeBillingViaPlatformChanges();
    }

    const selectedVariantId = this.form().controls.variant.value as string;
    this.selectedVariant.set(this.selectedVariant() ?? this.selectedServiceVariant(selectedVariantId));
  }

  private getUpgradableVariantOptions(): MarkantSelectOption[] {
    return (this.upgradeCandidates() as CartridgeVariant[]).map((variant: CartridgeVariant) => ({
      value: variant.variantId,
      representation: variant.name,
    }));
  }

  private getBillingViaPlatformOptions(): MarkantSelectOption[] {
    return [
      {
        value: 'true',
        representation: this.translocoService.translate('common.yes'),
      },
      {
        value: 'false',
        representation: this.translocoService.translate('common.no'),
      },
    ];
  }

  onVariantSelect(event: Event): void {
    const selectedVariantId = (event as CustomEvent).detail;
    const selectedCandidateVariant = (this.upgradeCandidates() as CartridgeVariant[]).find(
      variant => variant.variantId === selectedVariantId
    );

    if (!selectedCandidateVariant) {
      return;
    }
    this.selectedVariant.set(selectedCandidateVariant);

    const currentBookingIsPlatformBilled = !!(this.booking() as BookingDataWithUpgradeInfo).platformBilling;
    this.updateCanEditBillingViaPlatform(currentBookingIsPlatformBilled);
    this.updateCanEditIndividualPrice(!!this.selectedVariantPrice()?.hasToBeRequested);
    this.updateIsIndividualPriceRequired(this.form().controls.billingViaPlatform.value as string);
  }

  get variant(): FormControl<string | null> {
    return this.form().controls.variant;
  }

  get billingViaPlatform(): FormControl<string | null> {
    return this.form().controls.billingViaPlatform;
  }

  get individualPrice(): FormControl<string | null> {
    return this.form().controls.individualPrice;
  }

  get bookingReference(): FormControl<string | null> {
    return this.form().controls.bookingReference;
  }

  get billingViaPlatformControlOnReadonly(): FormControl<string> {
    return this.form().controls.billingViaPlatform as FormControl<string>;
  }

  get isEditMode(): boolean {
    return this.mode() === 'edit';
  }

  preventScroll(event: WheelEvent): void {
    event.preventDefault();
  }

  private updateCanEditBillingViaPlatform(currentBookingIsPlatformBilled: boolean): void {
    const { billingViaPlatform } = this.form().controls;

    billingViaPlatform.disable();
    billingViaPlatform.setValue(`${currentBookingIsPlatformBilled}`);
  }

  private selectedServiceVariant(variantId: string): CartridgeVariant | undefined {
    return (this.upgradeCandidates() as CartridgeVariant[]).find(variant => variant.variantId === variantId);
  }

  private getSelectedVariantPrice(variant: CartridgeVariant | undefined): Price | null {
    if (!variant) {
      return null;
    }

    const hasPrices = variant.prices?.length;
    const { organizationType, ezrCustomer } = this.organization() as OrganizationDetailData;

    const priceMatchingTargetAudience = (variant.prices || []).find(
      price => price.targetAudience.organizationType === organizationType && price.targetAudience.ezr === ezrCustomer
    );

    return hasPrices && priceMatchingTargetAudience ? priceMatchingTargetAudience : variant.price;
  }

  private onBillingViaPlatformSelect(answer: 'true' | 'false'): void {
    if (!this.selectedVariantPrice() || !this.selectedVariantPrice()?.hasToBeRequested) {
      return;
    }
    this.updateIsIndividualPriceRequired(answer);
  }

  private observeBillingViaPlatformChanges(): void {
    this.form()
      .controls.billingViaPlatform.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
        if (!value) {
          return;
        }
        this.onBillingViaPlatformSelect(value as 'true' | 'false');
      });
  }

  private updateCanEditIndividualPrice(shouldAllowEditing: boolean): void {
    if (shouldAllowEditing) {
      this.form().controls.individualPrice.setValue(null);
      this.form().controls.individualPrice.enable();
    } else {
      this.form().controls.individualPrice.setValue('N/A');
      this.form().controls.individualPrice.disable();
    }
    this.form().controls.individualPrice.updateValueAndValidity();
  }

  private updateIsIndividualPriceRequired(answer: string): void {
    if (answer === 'false') {
      this.form().controls.individualPrice.clearValidators();
    } else {
      this.form().controls.individualPrice.setValidators([Validators.required]);
    }
    this.form().controls.individualPrice.updateValueAndValidity();
  }
}
